import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, ArrowRight, Palette, Eye, Award, Shield } from "lucide-react";

const C = {
  bg: "#070707", bg2: "#11100E", card: "#181614", text: "#F5F2EB", text2: "#B5B0A7",
  gold: "#C9A055", red: "#FF3B30", bSub: "rgba(245,242,235,0.10)", bGold: "rgba(201,160,85,0.35)",
};
const F = { brush: "'Permanent Marker', cursive", cond: "'Saira Condensed', sans-serif", hand: "'Caveat', cursive", sans: "'DM Sans', 'Outfit', sans-serif" };

function useVisible(threshold = 0.15) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold });
    obs.observe(ref.current);
    const timer = setTimeout(() => setV(true), 600);
    return () => { obs.disconnect(); clearTimeout(timer); };
  }, [threshold]);
  return [ref, v];
}

const MarkerStroke = ({ color = C.gold, width = "100%", height = "6px", className = "" }) => (
  <div className={className} style={{ width, height, background: color, borderRadius: "3px", transform: "rotate(-0.8deg)", opacity: 0.85, boxShadow: `0 0 12px ${color}40` }} />
);

export default function MCOriginals() {
  useSEO({ title: "Originals — Marker Collection", description: "One-of-one original marker drawings. Hand-drawn, signed, and comes with a Certificate of Authenticity." });
  const navigate = useNavigate();
  const [originals, setOriginals] = useState([]);
  const [pageInfo, setPageInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [heroRef, heroVis] = useVisible(0.1);
  const [gridRef, gridVis] = useVisible(0.08);

  const [show, setShow] = useState(false);
  useEffect(() => { if (!loading) { const t = setTimeout(() => setShow(true), 100); return () => clearTimeout(t); } }, [loading]);

  useEffect(() => {
    Promise.all([
      http.get("/marker-collection/public/products"),
      http.get("/marker-collection/public/pages"),
    ]).then(([a, p]) => {
      setOriginals((a.data || []).filter(x => x.art_type === "Original" && !x.linked_parent_id));
      setPageInfo(p.data?.originals || {});
      if (p.data?.originals?.enabled === false) navigate("/marker-collection");
    }).catch(() => {}).finally(() => setLoading(false));
  }, [navigate]);

  if (loading) return (
    <Layout><div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
      <div className="w-16 h-16 border-2 animate-spin" style={{ borderColor: `${C.gold}30`, borderTopColor: C.gold }} />
    </div></Layout>
  );

  const available = originals.filter(a => a.original_status !== "sold" && a.original_status !== "Sold");
  const sold = originals.filter(a => a.original_status === "sold" || a.original_status === "Sold");

  return (
    <Layout>
      <div style={{ background: C.bg, color: C.text, minHeight: "100vh" }}>
        <style>{`
          .mc-grit::before { content:''; position:absolute; inset:0; width:100%; height:100%; background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"); pointer-events:none; z-index:0; }
          .mc-card-orig { transition: all 0.5s cubic-bezier(0.25,0.46,0.45,0.94); }
          .mc-card-orig:hover { transform: translateY(-6px); }
          .mc-card-orig:hover img { transform: scale(1.04); }
        `}</style>

        {/* Hero */}
        <section ref={heroRef} className="relative mc-grit overflow-hidden" style={{ background: C.bg, borderBottom: `1px solid ${C.bSub}` }} data-testid="mc-originals-hero">
          <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 relative z-10">
            <Link to="/marker-collection" className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold mb-6 hover:opacity-70 transition-opacity"
              style={{ fontFamily: F.sans, color: C.gold }} data-testid="mc-originals-back">
              <ArrowLeft size={14} /> Back to Home
            </Link>
            <div className={`transition-all duration-700 ${show || heroVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <span className="inline-block text-[10px] tracking-[0.4em] uppercase font-bold px-4 py-1.5 mb-4" style={{ fontFamily: F.sans, background: C.red, color: "#fff" }}>1 OF 1 — HAND-DRAWN</span>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.85] tracking-tight mb-4" style={{ fontFamily: F.cond, color: C.text }} data-testid="mc-originals-title">
                {pageInfo?.heading || "ONE DRAWING.\nONE ORIGINAL."}
              </h1>
              <MarkerStroke width="80px" className="mb-5" />
              <p className="text-base max-w-xl leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }}>
                {pageInfo?.text || "The actual hand-drawn originals. One of one. Signed, titled, and comes with a Certificate of Authenticity. When it's gone, it's gone forever."}
              </p>
            </div>
          </div>
        </section>

        {/* What You Get */}
        <section className="relative mc-grit" style={{ background: C.bg2, borderBottom: `1px solid ${C.bSub}` }} data-testid="mc-originals-perks">
          <div className="max-w-7xl mx-auto px-6 py-12 relative z-10">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                [Award, "CERTIFICATE OF AUTHENTICITY", "Every original comes with a signed COA verifying it as the one and only."],
                [Shield, "ONE OF ONE", "When an original sells, it's gone. No reprints, no second chances."],
                [Palette, "HAND-DRAWN", "Every line, dot, and shadow is made by hand with professional markers."],
              ].map(([Icon, title, desc], i) => (
                <div key={i} className="flex gap-4 items-start p-5" style={{ background: `${C.gold}05`, border: `1px solid ${C.bGold}` }}>
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center" style={{ background: `${C.gold}10`, border: `1px solid ${C.bGold}` }}>
                    <Icon size={18} style={{ color: C.gold }} />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.25em] uppercase font-bold mb-1" style={{ fontFamily: F.sans, color: C.text }}>{title}</p>
                    <p className="text-xs leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }}>{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="relative mc-grit" style={{ background: C.bg }} data-testid="mc-originals-story">
          <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 relative z-10">
            <div className="max-w-2xl mx-auto text-center">
              <p className="text-2xl sm:text-3xl leading-snug mb-6" style={{ fontFamily: F.hand, color: `${C.gold}70` }}>
                "Not reproductions. Pieces of me."
              </p>
              <p className="text-sm leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }}>
                {pageInfo?.subtext || "Each original is the real deal — the physical piece that was drawn by hand, mark by mark. No reproductions, no do-overs. Just the art exactly as it came to life."}
              </p>
            </div>
          </div>
        </section>

        {/* Available Originals */}
        <section ref={gridRef} className="relative mc-grit" style={{ background: C.bg2 }} data-testid="mc-originals-available">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-20 relative z-10">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase font-bold mb-2" style={{ fontFamily: F.sans, color: C.red }}>AVAILABLE NOW</p>
                <h2 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: F.cond, color: C.text }}>
                  {available.length} ORIGINAL{available.length !== 1 ? "S" : ""} AVAILABLE
                </h2>
              </div>
            </div>
            {available.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {available.map((art, i) => (
                  <Link key={art.id} to={`/marker-collection/${art.slug}`} className="mc-card-orig block relative group"
                    style={{ opacity: show || gridVis ? 1 : 0, transform: show || gridVis ? "translateY(0)" : "translateY(20px)", transition: `all 0.6s ease ${100 + i * 80}ms` }}
                    data-testid={`mc-originals-card-${art.slug}`}>
                    <div className="relative overflow-hidden" style={{ border: `3px solid ${C.bGold}`, background: C.card }}>
                      {art.main_image ? (
                        <img src={resolveImage(art.main_image)} alt={art.title} className="w-full aspect-[4/5] object-cover transition-transform duration-700" loading="lazy" />
                      ) : (
                        <div className="w-full aspect-[4/5] flex items-center justify-center" style={{ background: C.bg2 }}><Palette size={48} style={{ color: `${C.gold}20` }} /></div>
                      )}
                      <div className="absolute top-3 left-3 px-3 py-1 text-[9px] tracking-[0.3em] uppercase font-bold" style={{ fontFamily: F.sans, background: C.red, color: "#fff" }}>ORIGINAL</div>
                      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 100%)" }}>
                        <p className="text-base sm:text-lg font-bold mb-1" style={{ fontFamily: F.sans, color: C.text }}>{art.title}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-2xl font-black" style={{ fontFamily: F.cond, color: C.gold }}>${Number(art.price || 0).toFixed(0)}</span>
                          <span className="text-[9px] tracking-[0.2em] uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>{art.dimensions}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16" style={{ border: `2px dashed ${C.bSub}` }}>
                <Palette size={40} className="mx-auto mb-4" style={{ color: `${C.gold}30` }} />
                <p className="text-sm mb-2" style={{ fontFamily: F.sans, color: C.text2 }}>All originals are currently sold.</p>
                <Link to="/marker-collection/commissions" className="text-xs hover:underline" style={{ fontFamily: F.sans, color: C.gold }}>Request a custom commission instead</Link>
              </div>
            )}

            {/* Sold Originals */}
            {sold.length > 0 && (
              <div className="mt-16">
                <p className="text-[10px] tracking-[0.3em] uppercase font-bold mb-6" style={{ fontFamily: F.sans, color: C.text2 }}>PREVIOUSLY SOLD</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {sold.map(art => (
                    <Link key={art.id} to={`/marker-collection/${art.slug}`} className="block relative group opacity-60 hover:opacity-80 transition-opacity"
                      data-testid={`mc-originals-sold-${art.slug}`}>
                      <div className="relative overflow-hidden" style={{ border: `1px solid ${C.bSub}` }}>
                        {art.main_image ? (
                          <img src={resolveImage(art.main_image)} alt={art.title} className="w-full aspect-[3/4] object-cover grayscale" loading="lazy" />
                        ) : (
                          <div className="w-full aspect-[3/4] flex items-center justify-center" style={{ background: C.bg2 }}><Palette size={24} style={{ color: `${C.gold}15` }} /></div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)" }}>
                          <span className="text-xs tracking-[0.35em] uppercase font-black" style={{ fontFamily: F.cond, color: C.text }}>SOLD</span>
                        </div>
                      </div>
                      <p className="text-[10px] font-bold mt-1.5 truncate" style={{ fontFamily: F.sans, color: C.text2 }}>{art.title}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Commission CTA */}
        <section className="relative mc-grit" style={{ background: C.bg, borderTop: `1px solid ${C.bSub}` }} data-testid="mc-originals-cta">
          <div className="max-w-7xl mx-auto px-6 py-14 text-center relative z-10">
            <p className="text-2xl sm:text-3xl mb-4" style={{ fontFamily: F.hand, color: `${C.gold}60` }}>Want something made just for you?</p>
            <Link to="/marker-collection/commissions" className="inline-flex items-center gap-3 px-8 py-4 text-xs tracking-[0.25em] uppercase font-bold transition-all hover:gap-5"
              style={{ fontFamily: F.sans, background: C.gold, color: C.bg }} data-testid="mc-originals-commission-cta">
              REQUEST A COMMISSION <ArrowRight size={15} />
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
}
